import type { CmmdBoardTask } from "@cmmd-forge/contracts";
import { DataBoard, type DataBoardColumn } from "~/components/data-views/DataBoard";
import { getStatusConfig } from "~/components/command-center/statusConfig";
import { groupBoardTasksByStatus } from "~/components/projects/boardColumns";
import { cn } from "~/lib/utils";
import { toastManager } from "~/components/ui/toast";
import { ProjectTaskCard } from "./ProjectTaskCard";
import { ProjectTaskMilestoneSwimlaneBoard } from "./ProjectTaskMilestoneSwimlaneBoard";
import { canQuickCreateInWorkflowGroup } from "./projectTaskCreateOptions";
import {
  canDropTaskInWorkflowColumn,
  workflowGroupForTask,
  workflowGroupsForTasks,
} from "./taskWorkflowGroups";
import {
  activeTasksInOrder,
  EMPTY_PROJECT_MILESTONES,
  EMPTY_TASK_CONVERSATION_IDS,
  normalizeTaskStatus,
  type ProjectTaskBoardViewProps,
} from "./taskViewTypes";

function statusColumnLeading(statusKey: string) {
  const config = getStatusConfig(statusKey);
  return (
    <span
      className={cn("inline-flex size-3 shrink-0 rounded-full border-2", config.circleClass)}
      aria-hidden="true"
    />
  );
}

/**
 * Kanban board for a project's tasks, built on the reusable DataBoard. Drag a
 * card within a column to reorder or across columns to change status; both
 * route through onMoveTask, which applies an optimistic local order and
 * reconciles from the server (see useProjectTaskBoard). Empty core status
 * columns stay visible as drop targets (unlike the list, which hides them).
 */
export function ProjectTaskBoardView({
  tasks,
  threadIndex,
  pendingTaskIds,
  conversationTaskIds = EMPTY_TASK_CONVERSATION_IDS,
  onOpenTask,
  onMoveTask,
  onOpenThread,
  workItemPrefix,
  onCreateInGroup,
  onCreateInLane,
  onManageMilestone,
  groupBy = "status",
  milestones = EMPTY_PROJECT_MILESTONES,
  workflow = null,
  reorderingDisabled = false,
  mutationDisabled = false,
}: ProjectTaskBoardViewProps) {
  const ordered = activeTasksInOrder(tasks);
  const boardTasks = ordered.filter((task) => {
    const group = workflowGroupForTask(task, workflow);
    const stage = workflow?.stages.find((candidate) => candidate.id === group.key);
    return stage?.category !== "backlog" && group.key !== "backlog";
  });
  if (groupBy === "milestone") {
    return (
      <ProjectTaskMilestoneSwimlaneBoard
        tasks={boardTasks}
        threadIndex={threadIndex}
        pendingTaskIds={pendingTaskIds}
        conversationTaskIds={conversationTaskIds}
        onOpenTask={onOpenTask}
        onMoveTask={onMoveTask}
        onOpenThread={onOpenThread}
        workItemPrefix={workItemPrefix}
        milestones={milestones}
        onCreateInGroup={onCreateInGroup}
        onCreateInLane={onCreateInLane}
        onManageMilestone={onManageMilestone}
        workflow={workflow}
        reorderingDisabled={reorderingDisabled}
        mutationDisabled={mutationDisabled}
      />
    );
  }
  // groupBoardTasksByStatus decides which columns show (always-show cores +
  // populated optionals); map them to the DataBoard column config.
  const stageById = new Map(workflow?.stages.map((stage) => [stage.id, stage]) ?? []);
  const columns: ReadonlyArray<DataBoardColumn> = workflow
    ? [
        ...workflow.stages
          .filter((stage) => stage.category !== "backlog")
          .map((stage) => ({
            id: stage.id,
            label: stage.name,
            leading: (
              <span
                className="inline-flex size-3 shrink-0 rounded-full border border-border"
                style={{ backgroundColor: stage.color.startsWith("#") ? stage.color : undefined }}
                aria-hidden="true"
              />
            ),
          })),
        ...workflowGroupsForTasks(boardTasks, workflow)
          .filter((group) => !stageById.has(group.key) && group.key !== "backlog")
          .map((group) => ({ id: group.key, label: group.label, leading: group.leading })),
      ]
    : groupBoardTasksByStatus(boardTasks)
        .filter((column) => column.status !== "backlog")
        .map((column) => ({
          id: column.status,
          label: column.label,
          leading: statusColumnLeading(column.status),
        }));

  return (
    <DataBoard<CmmdBoardTask>
      columns={columns}
      cards={boardTasks}
      cardId={(task) => String(task.id)}
      columnOf={(task) => workflowGroupForTask(task, workflow).key}
      onCardClick={(task) => onOpenTask(task.id)}
      onMove={(cardId, move) => {
        const task = boardTasks.find((candidate) => String(candidate.id) === cardId);
        if (task != null && !canDropTaskInWorkflowColumn(task, move.toColumnId, workflow)) {
          toastManager.add({
            type: "warning",
            title: "Transition not allowed",
            description: "Move this task through an allowed workflow stage first.",
          });
          return;
        }
        onMoveTask(Number(cardId), {
          kind: "position",
          move: {
            toStatus:
              stageById.get(move.toColumnId)?.category ?? normalizeTaskStatus(move.toColumnId),
            ...(stageById.has(move.toColumnId) ? { toStageId: move.toColumnId } : {}),
            ...(move.afterCardId ? { afterTaskId: Number(move.afterCardId) } : {}),
          },
        });
      }}
      onCreateInColumn={
        onCreateInGroup != null ? (columnId) => onCreateInGroup(columnId) : undefined
      }
      canCreateInColumn={(column) =>
        canQuickCreateInWorkflowGroup(workflow, column.id, mutationDisabled)
      }
      createInColumnLabel={(column) => `Add task in ${column.label}`}
      renderCard={(task) => (
        <ProjectTaskCard
          task={task}
          threadIndex={threadIndex}
          pending={pendingTaskIds.has(task.id)}
          hasConversation={conversationTaskIds.has(task.id)}
          onOpenTask={() => onOpenTask(task.id)}
          onOpenThread={onOpenThread}
          workItemPrefix={workItemPrefix}
        />
      )}
      reorderingDisabled={reorderingDisabled}
      mutationDisabled={mutationDisabled}
    />
  );
}
